import React, { useRef, useEffect, useState, useCallback } from 'react';
import LedPanel from '../LedPanel/LedPanel';
import styles from './LedDisplay.module.css';

// ── Pitch ─────────────────────────────────────────────────────────────────────
const PITCH = { dot: 2, gap: 1 };

// ── Mensajes por defecto ──────────────────────────────────────────────────────
const MSGS_A = [
  'PANTALLAS LED',
  '30 AÑOS DE EXPERIENCIA',
  'MICROLED ARGENTINA',
  'FABRICACIÓN 100% NACIONAL',
];
const MSGS_B = [
  'FULL COLOR · ROJO · ÁMBAR · VERDE',
  'TRANSPORTE · FARMACIAS · COMERCIOS',
  'DISEÑO Y ENSAMBLADO PROPIO',
  'INSTALACIÓN Y GARANTÍA',
];

// ── Modos de color ────────────────────────────────────────────────────────────
const COLOR_MODES = [
  { id: 'full',  label: 'Full Color', color: null,      hStart: null, hEnd: null },
  { id: 'red',   label: 'Rojo',       color: '#ff4444', hStart: 0,    hEnd: 8   },
  { id: 'amber', label: 'Ámbar',      color: '#ffaa00', hStart: 38,   hEnd: 45  },
  { id: 'green', label: 'Verde',      color: '#44ff44', hStart: 115,  hEnd: 125 },
  { id: 'blue',  label: 'Azul',       color: '#4499ff', hStart: 210,  hEnd: 220 },
  { id: 'white', label: 'Blanco',     color: '#ffffff', hStart: 0,    hEnd: 0   },
];

const FC_PALETTES = [
  { hStart: 0,   hEnd: 20  },
  { hStart: 195, hEnd: 245 },
  { hStart: 100, hEnd: 140 },
  { hStart: 265, hEnd: 310 },
  { hStart: 32,  hEnd: 58  },
  { hStart: 168, hEnd: 205 },
];

// ── Filtro de contenido ───────────────────────────────────────────────────────
const BAD_WORDS = [
  'puto','puta','pelotudo','boludo','concha','mierda','hijo de puta','hdp',
  'forro','trolo','pija','verga','coger','culero','pendejo','chorra','zorra',
  'garcha','fuck','shit','bitch','asshole','dick','cock','pussy','cunt',
  'bastard','motherfucker','nigger','faggot','whore','slut',
];
const filterText = t => BAD_WORDS.some(w => t.toLowerCase().includes(w));

// ── Sampler de texto (offscreen) ──────────────────────────────────────────────
const SCALE = 6;

function buildTextSampler(text, rows, font) {
  const m = document.createElement('canvas');
  m.width = 32000; m.height = rows * SCALE;
  const mCtx = m.getContext('2d');
  mCtx.font = font;
  const textPxW = Math.ceil(mCtx.measureText(text).width) + SCALE * 4;

  const off = document.createElement('canvas');
  off.width = Math.min(textPxW, 32000); off.height = rows * SCALE;
  const ctx = off.getContext('2d');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, off.width, off.height);
  ctx.fillStyle = '#fff';
  ctx.font = font;
  ctx.textBaseline = 'alphabetic';
  const fontPx = parseInt(font.match(/(\d+)px/)[1], 10) || (rows * SCALE * 0.74);
  ctx.fillText(text, SCALE * 2, (rows * SCALE) / 2 + fontPx * 0.36);

  const data = ctx.getImageData(0, 0, off.width, off.height).data;
  const W = off.width, H = off.height;

  const sample = (row, fracCol) => {
    const cx = Math.round(fracCol * SCALE + SCALE / 2);
    const cy = Math.round(row    * SCALE + SCALE / 2);
    let max = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const px = cx + dx, py = cy + dy;
        if (px < 0 || px >= W || py < 0 || py >= H) continue;
        const v = data[(py * W + px) * 4];
        if (v > max) max = v;
      }
    }
    return max;
  };

  return { sample, widthLed: Math.ceil(Math.min(textPxW, 32000) / SCALE) };
}

// ── Hook de animación ─────────────────────────────────────────────────────────
const PX_PER_SEC = 60;

const FPS_TARGET = 30; // 30fps — suave y mucho menos CPU que 60fps
const MS_PER_FRAME = 1000 / FPS_TARGET;

function useDisplay(canvasRef, msgsA, msgsB, colorMode, twoLines, running, brightness, speed, glow) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false }); // alpha:false = más rápido

    const DOT = PITCH.dot;
    const P   = DOT + PITCH.gap;

    const resize = () => {
      canvas.width  = canvas.offsetWidth  || 900;
      canvas.height = canvas.offsetHeight || 200;
    };
    resize();

    const COLS = Math.floor(canvas.width  / P);
    const ROWS = Math.floor(canvas.height / P);

    const rowsA = twoLines ? Math.floor(ROWS / 2) : ROWS;
    const rowsB = twoLines ? ROWS - rowsA : 0;
    const fontPxA = Math.max(Math.round(rowsA * SCALE * 0.74), SCALE * 2);
    const fontA   = `bold ${fontPxA}px Arial, Helvetica, sans-serif`;
    const fontPxB = twoLines ? Math.max(Math.round(rowsB * SCALE * 0.74), SCALE * 2) : fontPxA;
    const fontB   = `bold ${fontPxB}px Arial, Helvetica, sans-serif`;
    const SEP   = '   ·   ';
    const tickA = msgsA.join(SEP) + SEP;
    const tickB = twoLines ? msgsB.join(SEP) + SEP : tickA;
    const sampA = buildTextSampler(tickA, rowsA, fontA);
    const sampB = twoLines ? buildTextSampler(tickB, rowsB, fontB) : sampA;

    // ── Fondo pre-renderizado (se dibuja UNA sola vez, no cada frame) ──────────
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width  = canvas.width;
    bgCanvas.height = canvas.height;
    const bgCtx = bgCanvas.getContext('2d', { alpha: false });
    bgCtx.fillStyle = '#040604';
    bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
    bgCtx.fillStyle = 'rgba(255,255,255,0.018)';
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++) {
        bgCtx.beginPath();
        bgCtx.arc(c * P + DOT / 2, r * P + DOT / 2, DOT / 2, 0, Math.PI * 2);
        bgCtx.fill();
      }

    const speedPxPerSec = PX_PER_SEC * speed;
    let scrollA  = 0;
    let scrollB  = twoLines ? sampA.widthLed * P * 0.4 : 0;
    let lastTs   = null;
    let lastDraw = 0;
    let elapsed  = 0;
    let animId;

    const isFullColor = colorMode.id === 'full';
    const isWhite     = colorMode.id === 'white';
    const brightMul   = brightness / 100;
    const glowRadius  = glow * 0.04; // reducido para mejor performance

    const getHue = (c, fracCol, widthLed, t) => {
      if (isWhite) return null;
      if (!isFullColor) {
        return colorMode.hStart + (colorMode.hEnd - colorMode.hStart) * ((c / COLS + t * 0.12) % 1);
      }
      const loopCol = fracCol % widthLed;
      const idx     = Math.floor((loopCol / widthLed) * FC_PALETTES.length);
      const pal     = FC_PALETTES[idx % FC_PALETTES.length];
      return pal.hStart + (pal.hEnd - pal.hStart) * (((c / COLS) + t * 0.18) % 1);
    };

    const drawTextLine = (sampler, rowStart, rowCount, scrollPx) => {
      const { sample, widthLed } = sampler;
      // Deshabilitar shadow si glow es bajo — ahorra mucho CPU
      const useGlow = glowRadius > 1;
      for (let r = 0; r < rowCount; r++) {
        for (let c = 0; c < COLS; c++) {
          const rawCol  = (c * P + DOT / 2 + scrollPx) / P;
          const fracCol = ((rawCol % widthLed) + widthLed) % widthLed;
          const br = sample(r, fracCol);
          if (br < 20) continue;
          const x   = c * P + DOT / 2;
          const y   = (rowStart + r) * P + DOT / 2;
          const hue = getHue(c, fracCol, widthLed, elapsed);
          const baseLit = 65 + Math.round((br / 255) * 15);
          const lit = Math.round(baseLit * brightMul);
          if (isWhite) {
            const w = Math.round((72 + Math.round((br / 255) * 14)) * brightMul);
            if (useGlow) { ctx.shadowBlur = glowRadius; ctx.shadowColor = `hsl(0,0%,95%)`; }
            ctx.fillStyle = `hsl(0,0%,${w}%)`;
          } else {
            if (useGlow) { ctx.shadowBlur = glowRadius; ctx.shadowColor = `hsl(${hue},100%,75%)`; }
            ctx.fillStyle = `hsl(${hue},100%,${lit}%)`;
          }
          ctx.beginPath();
          ctx.arc(x, y, DOT / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      if (useGlow) ctx.shadowBlur = 0;
    };

    const draw = (ts) => {
      animId = requestAnimationFrame(draw);

      // Limitar a 30fps — skippear frames intermedios
      if (ts - lastDraw < MS_PER_FRAME) return;
      const dt = lastTs !== null ? Math.min((ts - lastTs) / 1000, 0.05) : 0;
      lastTs  = ts;
      lastDraw = ts;
      elapsed += dt;

      // Copiar fondo pre-renderizado (1 sola operación GPU vs ~20.000 arcs)
      ctx.drawImage(bgCanvas, 0, 0);

      if (running) {
        const delta = speedPxPerSec * dt;
        scrollA += delta;
        if (scrollA >= sampA.widthLed * P) scrollA -= sampA.widthLed * P;
        if (twoLines) {
          scrollB += delta * 0.85;
          if (scrollB >= sampB.widthLed * P) scrollB -= sampB.widthLed * P;
        }
      }

      drawTextLine(sampA, 0, rowsA, scrollA);
      if (twoLines) drawTextLine(sampB, rowsA, rowsB, scrollB);
    };

    animId = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, [canvasRef, msgsA, msgsB, colorMode, twoLines, running, brightness, speed, glow]);
}

// ── Chequeo de rendimiento ────────────────────────────────────────────────────
// Oculta el display en dispositivos de gama baja para no afectar la performance
function checkPerformance() {
  // 1. Respeta la preferencia de animación reducida del sistema
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  // 2. Menos de 4 núcleos de CPU → gama baja
  const cores = navigator.hardwareConcurrency || 4;
  if (cores < 4) return false;
  // 3. Menos de 4 GB de RAM (API disponible en Chrome/Edge, no en Firefox/Safari)
  const mem = navigator.deviceMemory;
  if (mem !== undefined && mem < 4) return false;
  // 4. Benchmark rápido: si el loop tarda > 2ms, es lento
  const t0 = performance.now();
  let x = 0;
  for (let i = 0; i < 100000; i++) x += Math.sin(i);
  const elapsed = performance.now() - t0;
  if (elapsed > 2) return false;
  return true;
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function LedDisplay() {
  const canvasRef    = useRef(null);
  const wrapRef      = useRef(null);
  const [capable]      = useState(() => checkPerformance());
  const [visible,      setVisible]    = useState(true);
  const [colorModeId,  setColorModeId] = useState('full');
  const [inputText,    setInputText]   = useState('');
  const [msgsA,        setMsgsA]       = useState(MSGS_A);
  const [msgsB,        setMsgsB]       = useState(MSGS_B);
  const [inputError,   setInputError]  = useState('');
  const [twoLines,     setTwoLines]    = useState(false);
  const [showAdv,      setShowAdv]     = useState(false);
  const [running,      setRunning]     = useState(true);
  const [brightness,   setBrightness]  = useState(85);
  const [speed,        setSpeed]       = useState(2);
  const [glow,         setGlow]        = useState(55);

  const colorMode = COLOR_MODES.find(c => c.id === colorModeId);

  // Pausar cuando no es visible en pantalla
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // running && visible: pausa si el usuario apagó o si está fuera del viewport
  useDisplay(canvasRef, msgsA, msgsB, colorMode, twoLines, capable && running && visible, brightness, speed, glow);

  const handleAddText = useCallback(() => {
    const trimmed = inputText.trim().toUpperCase();
    if (!trimmed) return;
    if (filterText(trimmed)) { setInputError('Por favor ingresá un mensaje apropiado.'); return; }
    if (trimmed.length > 100) { setInputError('Máximo 100 caracteres.'); return; }
    setMsgsA([trimmed]); setMsgsB([trimmed]);
    setInputText(''); setInputError('');
  }, [inputText]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputText(val);
    setInputError('');
    if (!val.trim()) { setMsgsA(MSGS_A); setMsgsB(MSGS_B); }
  };

  const handleReset = () => {
    setMsgsA(MSGS_A); setMsgsB(MSGS_B);
    setInputText(''); setInputError('');
  };

  // Ocultar en dispositivos de bajo rendimiento (todos los hooks ya se llamaron)
  if (!capable) return null;

  return (
    <div className={styles.wrap} ref={wrapRef}>

      <LedPanel canvasRef={canvasRef} />

      <div className={styles.controls}>

        {/* Líneas */}
        <div className={styles.controlRow}>
          <span className={styles.ctrlLabel}>Líneas</span>
          <div className={styles.lineToggle}>
            <button className={`${styles.lineBtn} ${!twoLines ? styles.lineBtnActive : ''}`} onClick={() => setTwoLines(false)}>▬ 1 línea</button>
            <button className={`${styles.lineBtn} ${twoLines  ? styles.lineBtnActive : ''}`} onClick={() => setTwoLines(true)}>☰ 2 líneas</button>
          </div>
        </div>

        {/* Color */}
        <div className={styles.controlRow}>
          <span className={styles.ctrlLabel}>Color</span>
          <div className={styles.swatches}>
            {COLOR_MODES.map(c => (
              <button key={c.id}
                className={`${styles.swatch} ${c.id === colorModeId ? styles.swatchActive : ''}`}
                onClick={() => setColorModeId(c.id)} title={c.label}>
                {c.color ? <span className={styles.swatchDot} style={{ background: c.color }} /> : <span className={styles.swatchRainbow} />}
                <span className={styles.swatchLabel}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Texto */}
        <div className={styles.controlRow}>
          <span className={styles.ctrlLabel}>Texto</span>
          <div className={styles.textRow}>
            <input className={styles.textInput} type="text" maxLength={100}
              placeholder="Escribí tu mensaje..." value={inputText}
              onChange={handleInputChange}
              onKeyDown={e => e.key === 'Enter' && handleAddText()} />
            <button className={styles.textBtn} onClick={handleAddText}>Mostrar</button>
            {msgsA !== MSGS_A && <button className={styles.resetBtn} onClick={handleReset} title="Restaurar">↺</button>}
          </div>
          {inputError && <p className={styles.inputError}>{inputError}</p>}
        </div>

        {/* Toggle avanzado */}
        <button className={styles.advToggle} onClick={() => setShowAdv(v => !v)}>
          {showAdv ? '▲ Menos opciones' : '▼ Más opciones'}
        </button>

        {showAdv && (
          <div className={styles.advPanel}>
            <div className={styles.advRow}>
              <button
                className={`${styles.powerBtn} ${running ? styles.powerOn : styles.powerOff}`}
                onClick={() => setRunning(v => !v)}>
                ⏻ {running ? 'Encendido' : 'Apagado'}
              </button>
            </div>
            <div className={styles.advRow}>
              <span className={styles.advLabel}>Brillo</span>
              <input type="range" min="20" max="100" value={brightness}
                onChange={e => setBrightness(+e.target.value)} className={styles.slider} />
              <span className={styles.advVal}>{brightness}%</span>
            </div>
            <div className={styles.advRow}>
              <span className={styles.advLabel}>Velocidad</span>
              <input type="range" min="1" max="5" step="0.5" value={speed}
                onChange={e => setSpeed(+e.target.value)} className={styles.slider} />
              <span className={styles.advVal}>{speed}x</span>
            </div>
            <div className={styles.advRow}>
              <span className={styles.advLabel}>Glow</span>
              <input type="range" min="0" max="100" value={glow}
                onChange={e => setGlow(+e.target.value)} className={styles.slider} />
              <span className={styles.advVal}>{glow}</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
